/*
 * Search UI for the support page (<sn-support-search>, sections/sn-support-search.liquid)
 * and the header search drawer (<sn-search-drawer>, sections/search-drawer.liquid).
 *
 * Both share SearchElement: typing, cancelling stale searches, loading state, results count,
 * FAQ accordions and arrow keys. Each element only adds its own layout.
 *
 * The engine (sn-search-core.js → window.SNSearch) loads on demand from data-core-url:
 * straight away on the support page, on first use in the drawer, so pages where nobody
 * searches don't download it.
 */
(() => {
  if (customElements.get('sn-search-drawer')) return;

  const debounce = (fn, ms) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); };
  };

  let corePromise = null;
  const loadCore = (url) => {
    if (window.SNSearch) return Promise.resolve(window.SNSearch);
    corePromise ??= new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = url;
      script.async = true;
      script.onload = () => resolve(window.SNSearch);
      script.onerror = () => {
        corePromise = null;
        reject(new Error('sn-search-core.js failed to load'));
      };
      document.head.appendChild(script);
    });
    return corePromise;
  };

  /* ---------- shared behaviour ---------- */

  // Subclasses set this.input, this.results, this.status, this.limits, this.debounceMs, this.idPrefix and this.productStyle,
  // then call setup(). They implement layout(result, groups, count), setLoading(on) and hide().
  class SearchElement extends HTMLElement {
    setup() {
      this.strings = JSON.parse(this.querySelector('[data-strings]')?.textContent || '{}');
      this.rawTopics = JSON.parse(this.querySelector('[data-topics]')?.textContent || 'null') || this.strings.topics_list || [];

      this.input.addEventListener('input', debounce(() => this.search(this.input.value), this.debounceMs));
      this.input.addEventListener('focus', () => this.ready().then((S) => S.loadIndex(this.dataset.indexUrl)));
      this.input.form.addEventListener('reset', () => this.clear());

      // While results are showing, the round submit button becomes "Clear" (its close icon is swapped in by CSS)
      this.field = this.input.closest('.sn-support-search__field');
      this.submitBtn = this.field?.querySelector('[data-submit]');
      this.submitBtn?.addEventListener('click', (e) => {
        if (!this.field.classList.contains('has-results')) return;
        e.preventDefault();
        this.input.form.reset();
      });
    }

    // Resolves once the engine is loaded and this element's renderer and topics are ready
    ready() {
      return loadCore(this.dataset.coreUrl).then((S) => {
        if (!this.renderer) {
          this.renderer = new S.Renderer({ strings: this.strings, idPrefix: this.idPrefix, productStyle: this.productStyle });
          this.topics = S.prepareTopics(this.rawTopics);
        }
        return S;
      });
    }

    async search(raw) {
      const query = raw.trim();
      if (query.length < 2) {
        this.clear();
        return;
      }
      this.controller?.abort();
      const controller = (this.controller = new AbortController());
      this.setLoading(true);
      try {
        const S = await this.ready();
        const result = await S.run({
          query,
          indexUrl: this.dataset.indexUrl,
          suggestUrl: this.dataset.suggestUrl,
          topics: this.topics,
          signal: controller.signal,
        });
        if (controller.signal.aborted || !result) return;
        const { groups, count } = this.renderer.groups(result, this.limits);
        this.layout(result, groups, count);
        S.fillSkus(this.results);
        if (this.status) {
          this.status.textContent = this.renderer.fill(count ? this.strings.results_count : this.strings.no_results, query, count);
        }
      } catch (err) {
        if (err.name !== 'AbortError') console.warn(`[${this.localName}]`, err);
      } finally {
        if (this.controller === controller) this.setLoading(false);
      }
    }

    clear() {
      this.controller?.abort();
      this.results.innerHTML = '';
      this.hide();
    }

    setResultsState(on) {
      if (this.inline) return; // inline (search page): the arrow always submits, to refresh the product grid
      this.field?.classList.toggle('has-results', on);
      if (this.submitBtn) this.submitBtn.setAttribute('aria-label', on ? this.submitBtn.dataset.labelClear : this.submitBtn.dataset.labelSearch);
    }

    navigate(e) {
      return window.SNSearch?.arrowNav(e, this.input, this.results);
    }
  }

  /* ---------- support page: hero bar with a results panel ---------- */

  class SnSupportSearch extends SearchElement {
    connectedCallback() {
      this.form = this.querySelector('[data-form]');
      this.input = this.querySelector('[data-input]');
      this.panel = this.querySelector('[data-panel]');
      this.results = this.querySelector('[data-results]');
      this.status = this.querySelector('[data-status]');
      this.resetBtn = this.querySelector('[data-reset]');
      this.allLink = this.querySelector('[data-all-link]');
      this.allLabel = this.querySelector('[data-all-label]');
      this.spinner = this.querySelector('.sn-support-search__spinner');
      this.idPrefix = this.input.id;
      this.productStyle = 'card';
      this.debounceMs = 180;
      // Inline (search page): results sit on the page under the bar and the theme's product grid lists products
      this.inline = this.dataset.layout === 'inline' || this.dataset.layout === 'support';
      this.setup();
      const limit = Number(this.strings.limit) || 4;
      this.limits = this.inline
        ? { topics: 3, faqs: limit + 2, guides: limit + 2, products: 0, articles: limit, recipes: limit, pages: 3 }
        : { topics: 3, faqs: limit + 1, guides: limit, products: limit, articles: 3, recipes: 3, pages: 3 };

      this.input.addEventListener('input', () => this.resetBtn.classList.toggle('hidden', !this.input.value));
      this.input.addEventListener('focus', () => { if (this.input.value.trim()) this.open(); });
      this.form.addEventListener('reset', () => {
        this.resetBtn.classList.add('hidden');
        this.input.focus();
      });
      this.addEventListener('keydown', (e) => this.onKeydown(e));
      this.querySelector('[data-backdrop]')?.addEventListener('click', () => this.close());
      this.querySelectorAll('[data-term]').forEach((chip) =>
        chip.addEventListener('click', () => {
          this.input.value = chip.dataset.term;
          this.resetBtn.classList.remove('hidden');
          this.input.focus();
          this.search(chip.dataset.term);
        })
      );
      this.onDocClick = (e) => { if (!this.inline && !this.contains(e.target) && !this.panel.hidden) this.close(); };
      document.addEventListener('click', this.onDocClick);

      // The support page is all about search, so fetch the engine now rather than on first use
      this.ready();

      // Deep link: /pages/support?q=filter
      const q = new URLSearchParams(location.search).get('q');
      if (q) {
        this.input.value = q;
        this.resetBtn.classList.remove('hidden');
        this.search(q);
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocClick);
    }

    open() {
      if (!this.results.childElementCount) return;
      // On phones, lift the search bar to the top so the results have room above the keyboard
      if (!this.inline && this.panel.hidden && window.matchMedia('(max-width: 767.98px)').matches) {
        this.form.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      // is-open dims the page and lifts the hero above the header: dropdown only
      if (!this.inline) this.classList.add('is-open');
      this.setResultsState(true);
    }

    close() {
      this.panel.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.classList.remove('is-open');
      this.setResultsState(false);
    }

    hide() {
      this.close();
    }

    setLoading(on) {
      this.spinner?.classList.toggle('hdt-hidden', !on);
      this.classList.toggle('is-loading', on);
    }

    layout(result, groups, count) {
      const r = this.renderer;
      const g = (key) => r.group(groups, key);
      if (this.inline) {
        // No answers or articles: show nothing here and let the product grid below speak for itself
        if (!count) {
          this.results.innerHTML = '';
          this.close();
          return;
        }
        this.results.innerHTML = `
          <div class="sn-ss-columns">
            <div class="sn-ss-col sn-ss-col--help">${g('topics')}${g('faqs')}${g('guides')}</div>
            <div class="sn-ss-col sn-ss-col--read">${g('articles')}${g('recipes')}${g('pages')}</div>
          </div>`;
        this.results.querySelectorAll('.sn-ss-col').forEach((col) => { if (!col.innerHTML.trim()) col.remove(); });
        this.open();
        return;
      }
      if (!count) {
        this.results.innerHTML = r.emptyHtml(result.query);
      } else {
        this.results.innerHTML = `
          <div class="sn-ss-columns">
            <div class="sn-ss-col sn-ss-col--shop">${g('products')}</div>
            <div class="sn-ss-col sn-ss-col--help">${g('topics')}${g('faqs')}${g('guides')}</div>
            <div class="sn-ss-col sn-ss-col--read">${g('articles')}${g('recipes')}${g('pages')}</div>
          </div>`;
        this.results.querySelectorAll('.sn-ss-col').forEach((col) => { if (!col.innerHTML.trim()) col.remove(); });
      }
      this.allLink.href = `${this.dataset.searchUrl}?q=${encodeURIComponent(result.query)}&options%5Bprefix%5D=last`;
      this.allLabel.textContent = r.fill(this.strings.view_all, result.query, count);
      this.results.scrollTop = 0;
      this.open();
    }

    onKeydown(e) {
      if (e.key === 'Escape') {
        if (!this.inline && !this.panel.hidden) {
          e.preventDefault();
          this.input.focus();
          this.close();
        }
        return;
      }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      if (this.panel.hidden) {
        if (e.target === this.input && e.key === 'ArrowDown' && this.results.childElementCount) this.open();
        else return;
      }
      this.navigate(e);
    }
  }

  /* ---------- header search drawer: results under the theme's drawer search form ---------- */

  // The drawer shell, reset button and empty state are the theme's (<hdt-search-form>). This element fills the
  // results area and sets the attributes the theme's own predictive search used (open / loading / results on
  // .hdt-predictive-search) so the drawer CSS keeps working.
  class SnSearchDrawer extends SearchElement {
    connectedCallback() {
      this.host = this.closest('.hdt-search-modal__form');
      this.input = this.host.querySelector('input[type="search"][name="q"]');
      this.results = this.querySelector('[data-results]');
      this.status = this.host.querySelector('.hdt-predictive-search-status');
      this.footer = document.querySelector('#Drawer-search [all="predictive-search-form"]');
      this.idPrefix = 'sn-drawer';
      this.productStyle = 'drawer';
      this.debounceMs = 250;
      this.limits = { products: 4, topics: 2, faqs: 3, guides: 3, articles: 3, recipes: 3, pages: 2 };
      this.setup();

      this.host.addEventListener('keydown', (e) => {
        if (this.host.hasAttribute('results')) this.navigate(e);
      });

      // A term already in the box (e.g. on the search results page): search once the drawer's input is used
      if (this.input.value.trim().length >= 2) {
        this.input.addEventListener('focus', () => {
          if (!this.host.hasAttribute('results')) this.search(this.input.value);
        }, { once: true });
      }
    }

    hide() {
      this.host.removeAttribute('results');
      this.host.removeAttribute('open');
      this.host.removeAttribute('loading');
      this.input.setAttribute('aria-expanded', 'false');
      this.footer?.setAttribute('hidden', '');
      this.setResultsState(false);
    }

    setLoading(on) {
      this.host.toggleAttribute('loading', on);
      if (on && this.status) this.status.textContent = this.host.dataset.loadingText || '';
    }

    layout(result, groups, count) {
      const r = this.renderer;
      // One column: shopping first (people opening the header search are usually shopping), then help, then reading.
      // A question with a clear Best answer puts the answer first so it isn't pushed below the fold.
      const order = result.local.bestFaq
        ? ['faqs', 'topics', 'products', 'guides', 'articles', 'recipes', 'pages']
        : ['products', 'topics', 'faqs', 'guides', 'articles', 'recipes', 'pages'];
      this.results.innerHTML = count
        ? `<div class="sn-search-drawer__results">${order.map((key) => r.group(groups, key)).join('')}</div>`
        : r.emptyHtml(result.query);

      this.host.setAttribute('results', 'true');
      this.host.setAttribute('open', '');
      this.input.setAttribute('aria-expanded', 'true');
      this.footer?.toggleAttribute('hidden', !count);
      this.setResultsState(true);
    }
  }

  customElements.define('sn-support-search', SnSupportSearch);
  customElements.define('sn-search-drawer', SnSearchDrawer);

  // The support hub (sn-support-hub.js) builds on the support page element
  window.SNSearchElements = { SearchElement, SnSupportSearch, SnSearchDrawer };
})();
